import React, { useContext, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import CheckoutPageContext from '../../../context/CheckoutPageContext';
import { SHIP_MODE_TYPE } from '../../../tools/constants';
import SavedAddressCard from '../ShippingAddress/SavedAddress/SavedAddressCard';
import { getEstimatedDeliveryDateDisplay } from '../ShippingSpeed/HomeDeliveryShippingSpeedOption';
import CheckoutSelectedStore from '../CheckoutSelectedStore';
import { getPickupOptionsText } from '../ShippingSpeed/PickupShippingSpeedOption';
import Tmnt from '../../Tmnt/Tmnt';
import decodeHTMLEntities from '../../../tools/decodeHtml';

function DeliveryOverview() {
  const { control } = useFormContext();
  const { checkoutPageState } = useContext(CheckoutPageContext);
  const {
    shipMethod: { shipModeType = '' } = {},
    shippingSpeed,
    textFor = {},
    bagItems = {},
  } = checkoutPageState || {};
  const hasOnlyEGiftCard = bagItems?.hasOnlyEGiftCard;
  const selectedShippingSpeed = shippingSpeed?.find((item) => item.isSelected);
  const isHomeDelivery = useMemo(
    () => [
      SHIP_MODE_TYPE.HOME_DELIVERY,
      SHIP_MODE_TYPE.SAME_DAY_DELIVERY,
      SHIP_MODE_TYPE.DIRECT_TO_DESK,
    ].includes(shipModeType),
    [shipModeType],
  );
  const pickupType = shipModeType?.toLowerCase();
  const addressType = isHomeDelivery ? 'homeDelivery' : pickupType;

  const [
    firstName,
    lastName,
    address1,
    address2,
    city,
    district,
    state,
    zone,
    postalCode,
    poBox,
    prefecture,
    country,
  ] = useWatch({
    control,
    name: [
      `addresses.${addressType}.firstName`,
      `addresses.${addressType}.lastName`,
      `addresses.${addressType}.address1`,
      `addresses.${addressType}.address2`,
      `addresses.${addressType}.city`,
      `addresses.${addressType}.district`,
      `addresses.${addressType}.state`,
      `addresses.${addressType}.zone`,
      `addresses.${addressType}.postalCode`,
      `addresses.${addressType}.poBox`,
      `addresses.${addressType}.prefecture`,
      `addresses.${addressType}.country`,
    ],
  });

  const estimatedDeliveryDateDisplay = useMemo(
    () => (selectedShippingSpeed
      ? getEstimatedDeliveryDateDisplay(selectedShippingSpeed) : ''),
    [selectedShippingSpeed],
  );
  const pickupShippingSpeedText = getPickupOptionsText(selectedShippingSpeed, textFor);
  const shippingSpeedData = isHomeDelivery ? selectedShippingSpeed : pickupShippingSpeedText;

  const label = useMemo(() => {
    if (shippingSpeedData.description && shippingSpeedData.amountDisplay) {
      return decodeHTMLEntities(`${shippingSpeedData.description} - ${shippingSpeedData.amountDisplay}`);
    }
    return '';
  }, [shippingSpeedData]);

  const { inStockItemsOnlyTMNTValue, estDeliveryTMNTValue } = textFor;

  const savedAddress = {
    firstName,
    lastName,
    address1,
    address2,
    city,
    district,
    state,
    zone,
    postalCode,
    poBox,
    prefecture,
  };

  if (hasOnlyEGiftCard) {
    return (
      <Tmnt tmnt={textFor.eGiftCardDeliveryMessage} />
    );
  }
  return (
    <div className="shipping-pickup-overview" data-testid="shipping-pickup-overview">
      {isHomeDelivery ? (
        <>
          <Tmnt tmnt={textFor.shipping} attributes={{ className: 'overview-heading' }} />
          <SavedAddressCard address={savedAddress} isCountryFlagVisible />
          <p>{country || checkoutPageState?.shippingAddress?.country}</p>
          <div className="overview-container" data-testid="shipping-speed-content-overview">
            <Tmnt
              tmnt={textFor.shippingSpeedLegendTMNTValue}
              attributes={{ className: 'overview-heading' }}
            />
            <p>{`${estDeliveryTMNTValue.value}: ${estimatedDeliveryDateDisplay} ${inStockItemsOnlyTMNTValue.value}`}</p>
            <p data-testid="shipping-label">{label}</p>
          </div>
        </>
      ) : (
        <>
          <Tmnt tmnt={textFor.pickup} attributes={{ className: 'overview-heading' }} />
          <p>{`${firstName} ${lastName}`}</p>
          <p data-testid="pickup-label">{label}</p>
          <p>{pickupShippingSpeedText.subText}</p>
          <CheckoutSelectedStore allowChangeStore={false} parentClass="overview-container" />
        </>
      )}
    </div>
  );
}

export default DeliveryOverview;
